import "./lib/runtime"
import PhotoSwipe from "photoswipe"
import PhotoSwipeLightbox from "photoswipe/lightbox"

let galleryLightboxes = []

function removeFlashMessage(flash) {
  if (!flash || flash.dataset.flashClosed === "true") return

  flash.dataset.flashClosed = "true"
  flash.classList.add("is-hiding")

  const remove = () => flash.remove()
  flash.addEventListener("transitionend", remove, { once: true })
  window.setTimeout(remove, 240)
}

function bindFlashDismiss() {
  document.querySelectorAll(".flash-close").forEach((button) => {
    if (button.dataset.boundFlashClose === "true") return

    button.dataset.boundFlashClose = "true"
    button.addEventListener("click", () => {
      removeFlashMessage(button.closest(".flash"))
    })
  })

  document.querySelectorAll(".flash[data-flash-auto-dismiss='true']").forEach((flash) => {
    if (flash.dataset.boundFlashAutoDismiss === "true") return

    flash.dataset.boundFlashAutoDismiss = "true"
    const timeout = Number.parseInt(flash.dataset.flashTimeout || "4000", 10)

    window.setTimeout(() => {
      removeFlashMessage(flash)
    }, Number.isNaN(timeout) ? 4000 : timeout)
  })
}

function bindLanguageSelector() {
  const closeLanguageSelector = (selector) => {
    const button = selector.querySelector(".lang-btn")
    const dropdown = selector.querySelector(".lang-dropdown")
    if (!button || !dropdown) return

    dropdown.classList.remove("active")
    button.setAttribute("aria-expanded", "false")
  }

  document.querySelectorAll("[data-language-selector]").forEach((selector) => {
    const button = selector.querySelector(".lang-btn")
    const dropdown = selector.querySelector(".lang-dropdown")
    if (!button || !dropdown || button.dataset.boundLangToggle === "true") return

    button.dataset.boundLangToggle = "true"
    button.addEventListener("click", (event) => {
      event.preventDefault()
      event.stopPropagation()

      const shouldOpen = !dropdown.classList.contains("active")
      document.querySelectorAll("[data-language-selector]").forEach((otherSelector) => {
        if (otherSelector !== selector) closeLanguageSelector(otherSelector)
      })

      dropdown.classList.toggle("active", shouldOpen)
      button.setAttribute("aria-expanded", shouldOpen ? "true" : "false")
    })

    dropdown.querySelectorAll("a").forEach((link) => {
      if (link.dataset.boundLangOption === "true") return

      link.dataset.boundLangOption = "true"
      link.addEventListener("click", () => closeLanguageSelector(selector))
    })
  })

  if (document.documentElement.dataset.boundLanguageSelectorDismiss === "true") return

  document.documentElement.dataset.boundLanguageSelectorDismiss = "true"

  document.addEventListener("click", (event) => {
    document.querySelectorAll("[data-language-selector]").forEach((selector) => {
      if (selector.contains(event.target)) return

      closeLanguageSelector(selector)
    })
  })

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return

    document.querySelectorAll("[data-language-selector]").forEach(closeLanguageSelector)
  })
}

function bindSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    if (anchor.dataset.boundSmoothScroll === "true") return

    anchor.dataset.boundSmoothScroll = "true"
    anchor.addEventListener("click", (event) => {
      const href = anchor.getAttribute("href")
      const target = href ? document.querySelector(href) : null
      if (!target) return

      event.preventDefault()
      target.scrollIntoView({ behavior: "smooth", block: "start" })
    })
  })
}

function animateCounter(counter) {
  const target = parseFloat(counter.dataset.count || "0")
  const isDecimal = counter.dataset.decimal === "true"
  counter.dataset.counterAnimated = "true"
  counter.textContent = isDecimal ? target.toFixed(1) : Math.round(target)
}

function bindStatsCounter() {
  const statsSection = document.querySelector(".stats-grid")
  if (!statsSection || statsSection.dataset.boundStatsObserver === "true") return

  statsSection.dataset.boundStatsObserver = "true"
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return

      statsSection.querySelectorAll(".stat-number").forEach(animateCounter)
      observer.disconnect()
    })
  }, { threshold: 0.5 })

  observer.observe(statsSection)
}

function setHeroMediaShellReady(isReady) {
  const heroShell = document.querySelector("[data-hero-media-shell]")
  if (!heroShell) return

  heroShell.classList.toggle("is-loading", !isReady)
  heroShell.classList.toggle("is-loaded", isReady)
  heroShell.dataset.heroMediaReady = isReady ? "true" : "false"
}

function mediaElementIsReady(element) {
  if (element instanceof HTMLImageElement) {
    return element.complete && element.naturalWidth > 0
  }

  if (element instanceof HTMLVideoElement) {
    return element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
  }

  return false
}

function setMediaShellReady(shell, isReady) {
  shell.classList.toggle("is-loading", !isReady)
  shell.classList.toggle("is-loaded", isReady)

  if (isReady && shell.closest("[data-hero-media-shell]")) {
    setHeroMediaShellReady(true)
  }
}

function bindMediaPlaceholders() {
  if (!document.body) return

  document.body.classList.add("media-loading-enabled")

  let heroHasPendingMedia = false
  let heroHasLoadedMedia = false

  document.querySelectorAll("[data-media-shell]").forEach((shell) => {
    const media = shell.querySelector("[data-media-reveal]")
    if (!media) return

    const markLoaded = () => setMediaShellReady(shell, true)

    if (shell.dataset.boundMediaShell !== "true") {
      shell.dataset.boundMediaShell = "true"

      if (media instanceof HTMLImageElement) {
        media.addEventListener("load", markLoaded)
        media.addEventListener("error", markLoaded)
      } else if (media instanceof HTMLVideoElement) {
        media.addEventListener("loadeddata", markLoaded)
        media.addEventListener("canplay", markLoaded)
        media.addEventListener("error", markLoaded)
      }
    }

    const isReady = mediaElementIsReady(media)
    setMediaShellReady(shell, isReady)

    if (shell.closest("[data-hero-media-shell]")) {
      const shellIsVisible = !shell.hidden && !shell.closest("[hidden]")
      if (shellIsVisible) {
        heroHasLoadedMedia ||= isReady
        heroHasPendingMedia ||= !isReady
      }
    }
  })

  if (heroHasLoadedMedia) {
    setHeroMediaShellReady(true)
  } else if (heroHasPendingMedia) {
    setHeroMediaShellReady(false)
  }
}

function prepareHeroVideo(video) {
  if (!video) return

  video.playsInline = true
  video.setAttribute("playsinline", "")
  video.setAttribute("webkit-playsinline", "true")
  video.setAttribute("x5-playsinline", "true")
}

async function attemptHeroVideoPlayback(video, { muted = true } = {}) {
  if (!video) return false

  prepareHeroVideo(video)
  video.muted = muted

  try {
    const playResult = video.play()
    if (playResult && typeof playResult.then === "function") {
      await playResult
    }

    video.dataset.playbackReady = "true"
    return true
  } catch (_error) {
    return false
  }
}

function bindLandingPrice() {
  const totalPrice = document.getElementById("totalPrice")
  if (!totalPrice || !totalPrice.dataset.guidePrice) return

  const value = Number(totalPrice.dataset.guidePrice)
  if (!Number.isNaN(value)) {
    totalPrice.textContent = `$${value.toFixed(2)}`
  }
}

function landingAnalyticsBody() {
  if (!document.body || document.body.dataset.landingAnalytics !== "true") return null
  return document.body
}

function currentLandingAnalyticsPath() {
  return `${window.location.pathname}${window.location.search}`
}

function sanitizeAnalyticsToken(value, fallback = "unknown") {
  const normalized = `${value || ""}`.trim().replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "")
  return normalized || fallback
}

function buildLandingAnalyticsPayload(extra = {}) {
  const body = landingAnalyticsBody()
  if (!body) return extra

  return {
    landing_page_slug: body.dataset.landingPageSlug || "",
    landing_page_variant: body.dataset.landingPageVariant || "",
    landing_page_locale: body.dataset.landingPageLocale || document.documentElement.lang || "",
    ...extra
  }
}

function trackLandingAnalyticsEvent(eventName, extra = {}) {
  const payload = buildLandingAnalyticsPayload(extra)

  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, {
      ...payload,
      transport_type: "beacon"
    })
  }

  if (typeof window.clarity === "function") {
    const clarityEventName = [
      "landing",
      sanitizeAnalyticsToken(eventName),
      sanitizeAnalyticsToken(extra.cta_location),
      sanitizeAnalyticsToken(extra.cta_name)
    ].filter((token, index, tokens) => token && !(index > 0 && token === "unknown" && tokens[index - 1] === "landing")).join("_")

    window.clarity("event", clarityEventName)
  }
}

function syncLandingAnalyticsPageContext() {
  const body = landingAnalyticsBody()
  if (!body || document.documentElement.hasAttribute("data-turbo-preview")) return

  const path = currentLandingAnalyticsPath()

  if (typeof window.clarity === "function") {
    window.clarity("set", "landing_page_slug", body.dataset.landingPageSlug || "")
    window.clarity("set", "landing_page_variant", body.dataset.landingPageVariant || "")
    window.clarity("set", "landing_page_locale", body.dataset.landingPageLocale || document.documentElement.lang || "")
  }

  if (typeof window.gtag !== "function") return
  if (window.__landingAnalyticsLastTrackedPath === path) return

  window.gtag("event", "page_view", {
    ...buildLandingAnalyticsPayload({
      page_title: document.title,
      page_location: window.location.href,
      page_path: path
    }),
    transport_type: "beacon"
  })
  window.__landingAnalyticsLastTrackedPath = path
}

function bindLandingAnalytics() {
  if (!landingAnalyticsBody()) return

  syncLandingAnalyticsPageContext()

  document.querySelectorAll("[data-analytics-cta]").forEach((element) => {
    if (element.dataset.boundLandingAnalyticsClick === "true") return

    element.dataset.boundLandingAnalyticsClick = "true"
    element.addEventListener("click", () => {
      trackLandingAnalyticsEvent("landing_cta_click", {
        cta_name: element.dataset.analyticsCta || "",
        cta_location: element.dataset.analyticsCtaLocation || ""
      })
    })
  })

  document.querySelectorAll("form[data-analytics-submit]").forEach((form) => {
    if (form.dataset.boundLandingAnalyticsSubmit === "true") return

    form.dataset.boundLandingAnalyticsSubmit = "true"
    form.addEventListener("submit", () => {
      trackLandingAnalyticsEvent("landing_inquiry_submit", {
        cta_name: form.dataset.analyticsSubmit || "",
        cta_location: form.dataset.analyticsCtaLocation || ""
      })
    })
  })
}

function bindInquirySubmitState() {
  const form = document.getElementById("inquiryForm")
  const button = document.getElementById("submitBtn")
  if (!form || !button || button.type !== "submit" || form.dataset.boundInquirySubmit === "true") return

  // Reset any persisted disabled state when the page is restored from browser cache.
  button.disabled = false

  form.dataset.boundInquirySubmit = "true"
  form.addEventListener("submit", () => {
    window.setTimeout(() => {
      button.disabled = true
    }, 10)
  })
}

function readSubmitButtonLabel(button) {
  if (button instanceof HTMLInputElement) return button.value
  return button.innerHTML
}

function writeSubmitButtonLabel(button, label) {
  if (button instanceof HTMLInputElement) {
    button.value = label
    return
  }

  button.innerHTML = label
}

function resetSubmitFeedbackState(form, button) {
  const originalLabel = button.dataset.submitFeedbackOriginalLabel
  if (originalLabel) {
    writeSubmitButtonLabel(button, originalLabel)
  }

  button.disabled = false
  button.classList.remove("is-submitting")
  button.setAttribute("aria-busy", "false")
  form.dataset.submitFeedbackSubmitted = "false"
}

function bindSubmitFeedbackForms() {
  document.querySelectorAll("form[data-submit-feedback='true']").forEach((form) => {
    const button = form.querySelector("button[type='submit'], input[type='submit']")
    if (!button) return

    if (!button.dataset.submitFeedbackOriginalLabel) {
      button.dataset.submitFeedbackOriginalLabel = readSubmitButtonLabel(button)
    }

    resetSubmitFeedbackState(form, button)
    if (form.dataset.boundSubmitFeedback === "true") return

    form.dataset.boundSubmitFeedback = "true"
    form.addEventListener("submit", (event) => {
      if (form.dataset.submitFeedbackSubmitted === "true") {
        event.preventDefault()
        return
      }

      form.dataset.submitFeedbackSubmitted = "true"

      const submittingText = button.dataset.submittingText
      if (submittingText) {
        writeSubmitButtonLabel(button, submittingText)
      }

      button.disabled = true
      button.classList.add("is-submitting")
      button.setAttribute("aria-busy", "true")
    })
  })
}

function bindAlertMessages() {
  document.querySelectorAll("[data-alert-message]").forEach((trigger) => {
    if (trigger.dataset.boundAlertMessage === "true") return

    trigger.dataset.boundAlertMessage = "true"
    trigger.addEventListener("click", (event) => {
      event.preventDefault()
      window.alert(trigger.dataset.alertMessage)
    })
  })
}

async function writeToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text)
    return
  }

  const textarea = document.createElement("textarea")
  textarea.value = text
  textarea.setAttribute("readonly", "")
  textarea.style.position = "absolute"
  textarea.style.left = "-9999px"
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand("copy")
  document.body.removeChild(textarea)
}

function bindCopyTextTriggers() {
  document.querySelectorAll("[data-copy-text]").forEach((trigger) => {
    if (trigger.dataset.boundCopyText === "true") return

    trigger.dataset.boundCopyText = "true"
    trigger.addEventListener("click", async (event) => {
      event.preventDefault()

      const text = trigger.dataset.copyText
      if (!text) return

      try {
        await writeToClipboard(text)
        window.alert(trigger.dataset.copySuccess || "已复制")
      } catch (_error) {
        window.prompt(trigger.dataset.copyPrompt || "请手动复制以下内容：", text)
      }
    })
  })
}

function syncChoiceTarget(targetId) {
  if (!targetId) return

  const target = document.getElementById(targetId)
  if (!target) return

  const buttons = Array.from(document.querySelectorAll(`[data-choice-button][data-choice-target="${targetId}"]`))
  if (!buttons.length) return

  const mode = buttons[0].dataset.choiceMode || "single"
  const activeButtons = buttons.filter((button) => button.classList.contains("is-active"))

  target.value = mode === "multiple"
    ? activeButtons.map((button) => button.dataset.choiceValue).join(",")
    : (activeButtons[0]?.dataset.choiceValue || "")

  target.dispatchEvent(new Event("input", { bubbles: true }))
  target.dispatchEvent(new Event("change", { bubbles: true }))
}

function syncChoiceButtonsFromTarget(targetId) {
  if (!targetId) return

  const target = document.getElementById(targetId)
  if (!target) return

  const selectedValues = new Set(
    `${target.value || ""}`
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
  )

  document.querySelectorAll(`[data-choice-button][data-choice-target="${targetId}"]`).forEach((button) => {
    button.classList.toggle("is-active", selectedValues.has(button.dataset.choiceValue))
  })
}

function bindChoiceButtons() {
  document.querySelectorAll("[data-choice-button]").forEach((button) => {
    if (button.dataset.boundChoiceButton === "true") return

    button.dataset.boundChoiceButton = "true"
    button.addEventListener("click", () => {
      const targetId = button.dataset.choiceTarget
      const mode = button.dataset.choiceMode || "single"
      const groupButtons = Array.from(document.querySelectorAll(`[data-choice-button][data-choice-target="${targetId}"]`))

      if (mode === "multiple") {
        button.classList.toggle("is-active")
      } else {
        groupButtons.forEach((groupButton) => {
          groupButton.classList.toggle("is-active", groupButton === button)
        })
      }

      syncChoiceTarget(targetId)
    })
  })

  document.querySelectorAll("[data-choice-button][data-choice-target]").forEach((button) => {
    syncChoiceTarget(button.dataset.choiceTarget)
  })
}

function updateTextareaCounter(textarea) {
  const counter = textarea.parentElement?.querySelector(".hidden-gate-textarea-meta span")
  if (!counter) return

  counter.textContent = `${textarea.value.length}/${textarea.maxLength || 200}`
}

function bindTextareaCounters() {
  document.querySelectorAll("[data-visible-notes]").forEach((textarea) => {
    if (textarea.dataset.boundTextareaCounter === "true") return

    textarea.dataset.boundTextareaCounter = "true"
    updateTextareaCounter(textarea)
    textarea.addEventListener("input", () => updateTextareaCounter(textarea))
  })
}

function bindMobileDatePlaceholders() {
  document.querySelectorAll("input[data-mobile-date-placeholder='true']").forEach((input) => {
    const wrapper = input.closest("[data-date-placeholder-wrapper]")
    if (!wrapper) return

    const syncState = () => {
      wrapper.classList.toggle("is-empty", !`${input.value || ""}`.trim())
      const wrap = wrapper.querySelector(".hidden-gate-date-input-wrap")
      if (wrap && !wrap.dataset.placeholder) {
        wrap.dataset.placeholder = input.getAttribute("placeholder") || ""
      }
    }

    syncState()
    window.requestAnimationFrame(syncState)
    window.setTimeout(syncState, 0)

    if (input.dataset.boundMobileDatePlaceholder === "true") return

    input.dataset.boundMobileDatePlaceholder = "true"
    input.addEventListener("input", syncState)
    input.addEventListener("change", syncState)
    input.addEventListener("blur", syncState)
    input.addEventListener("focus", syncState)
  })
}

function hiddenGateDraftStorage() {
  try {
    return window.sessionStorage
  } catch (_error) {
    return null
  }
}

function hiddenGateDraftStorageKey(form) {
  return form?.dataset.draftStorageKey || ""
}

function hiddenGateDraftFields(form) {
  return Array.from(form.querySelectorAll("[data-draft-field]"))
}

function readHiddenGateDraft(form) {
  const storage = hiddenGateDraftStorage()
  const storageKey = hiddenGateDraftStorageKey(form)
  if (!storage || !storageKey) return null

  try {
    const rawDraft = storage.getItem(storageKey)
    return rawDraft ? JSON.parse(rawDraft) : null
  } catch (_error) {
    storage.removeItem(storageKey)
    return null
  }
}

function writeHiddenGateDraft(form) {
  const storage = hiddenGateDraftStorage()
  const storageKey = hiddenGateDraftStorageKey(form)
  if (!storage || !storageKey) return

  const fields = hiddenGateDraftFields(form).reduce((draft, field) => {
    draft[field.dataset.draftField] = field.value
    return draft
  }, {})

  storage.setItem(storageKey, JSON.stringify(fields))
}

function clearHiddenGateDraft(formOrKey) {
  const storage = hiddenGateDraftStorage()
  if (!storage) return

  const storageKey = typeof formOrKey === "string" ? formOrKey : hiddenGateDraftStorageKey(formOrKey)
  if (!storageKey) return

  storage.removeItem(storageKey)
}

function hiddenGateDraftHasContent(form) {
  return hiddenGateDraftFields(form).some((field) => `${field.value || ""}`.trim().length > 0)
}

function restoreHiddenGateDraft(form) {
  const draft = readHiddenGateDraft(form)
  if (!draft) return

  const skipDraftFields = form.dataset.lightweightLead === "true" ? new Set([
    "departure_origin",
    "duration_days",
    "travelers_count",
    "travel_types",
    "hotel_requirements",
    "transport_options"
  ]) : null

  hiddenGateDraftFields(form).forEach((field) => {
    const draftField = field.dataset.draftField
    if (skipDraftFields?.has(draftField)) return
    if (!Object.prototype.hasOwnProperty.call(draft, draftField)) return

    field.value = draft[draftField]
  })

  const choiceTargets = new Set(
    Array.from(form.querySelectorAll("[data-choice-button][data-choice-target]"))
      .map((button) => button.dataset.choiceTarget)
      .filter(Boolean)
  )

  choiceTargets.forEach((targetId) => {
    syncChoiceButtonsFromTarget(targetId)
    syncChoiceTarget(targetId)
  })

  form.querySelectorAll("[data-visible-notes]").forEach(updateTextareaCounter)
}

function clearHiddenGateDraftFromPageState() {
  const storageKey = document.body?.dataset.clearHiddenGateDraftKey
  if (!storageKey) return

  clearHiddenGateDraft(storageKey)
}

function bindHiddenGateForm() {
  const form = document.getElementById("hiddenGateInquiryForm")
  const button = document.getElementById("submitBtn")
  if (!form || !button || form.dataset.boundHiddenGateForm === "true") return

  const requiredFields = Array.from(form.querySelectorAll("[required], [data-required-selection='true']")).filter((field) => {
    if (form.dataset.lightweightLead === "true") {
      return field.matches("[required]")
    }

    return true
  })
  const fieldHasValue = (field) => {
    const rawValue = typeof field.value === "string" ? field.value.trim() : `${field.value || ""}`.trim()
    if (!rawValue) return false

    if (field.type === "number") {
      const numericValue = Number(rawValue)
      if (Number.isNaN(numericValue)) return false

      const minValue = field.getAttribute("min")
      if (minValue !== null && minValue !== "") {
        return numericValue >= Number(minValue)
      }

      return true
    }

    return true
  }

  const fieldIsValid = (field) => {
    if (!fieldHasValue(field)) return false
    return typeof field.checkValidity === "function" ? field.checkValidity() : true
  }

  const syncSubmitState = () => {
    if (button.dataset.processing === "true") return

    const shouldDisable = requiredFields.some((field) => !fieldIsValid(field))
    button.disabled = shouldDisable
    button.classList.toggle("is-disabled", shouldDisable)
    button.classList.remove("is-processing")
    button.setAttribute("aria-busy", "false")
  }

  const persistDraftAndState = () => {
    writeHiddenGateDraft(form)
    form.dataset.draftDirty = hiddenGateDraftHasContent(form) ? "true" : "false"
  }

  form.dataset.boundHiddenGateForm = "true"
  restoreHiddenGateDraft(form)
  persistDraftAndState()
  syncSubmitState()

  requiredFields.forEach((field) => {
    field.addEventListener("input", syncSubmitState)
    field.addEventListener("change", syncSubmitState)
    field.addEventListener("keyup", syncSubmitState)
    field.addEventListener("blur", syncSubmitState)
  })

  form.addEventListener("input", () => {
    syncSubmitState()
    persistDraftAndState()
  })
  form.addEventListener("change", () => {
    syncSubmitState()
    persistDraftAndState()
  })

  form.addEventListener("submit", (event) => {
    syncSubmitState()
    if (button.disabled) {
      event.preventDefault()
      form.reportValidity()
      return
    }

    form.dataset.hiddenGateSubmitted = "true"
    button.dataset.processing = "true"
    button.disabled = true
    button.classList.remove("is-disabled")
    button.classList.add("is-processing")
    button.setAttribute("aria-busy", "true")
  })
}

function bindSnapCarousels() {
  document.querySelectorAll("[data-snap-carousel]").forEach((carousel) => {
    if (carousel.dataset.boundSnapCarousel === "true") return

    const track = carousel.querySelector("[data-snap-track]")
    const dots = Array.from(carousel.querySelectorAll("[data-snap-dot]"))
    if (!track || !dots.length) return

    const updateActiveDot = () => {
      const slideWidth = track.clientWidth || 1
      const activeIndex = Math.round(track.scrollLeft / slideWidth)

      dots.forEach((dot, index) => {
        dot.classList.toggle("is-active", index === activeIndex)
      })
    }

    carousel.dataset.boundSnapCarousel = "true"
    track.addEventListener("scroll", () => {
      window.requestAnimationFrame(updateActiveDot)
    }, { passive: true })

    dots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const index = Number(dot.dataset.snapIndex || "0")
        track.scrollTo({ left: track.clientWidth * index, behavior: "smooth" })
      })
    })

    updateActiveDot()
  })
}

function bindCarouselCounters() {
  document.querySelectorAll("[data-carousel-counter]").forEach((carousel) => {
    if (carousel.dataset.boundCarouselCounter === "true") return

    const currentLabel = carousel.querySelector("[data-carousel-current]")
    const totalLabel = carousel.querySelector("[data-carousel-total-label]")
    const items = Array.from(carousel.querySelectorAll(".carousel-item"))
    if (!currentLabel || !totalLabel) return

    const total = Number.parseInt(carousel.dataset.carouselTotal || "0", 10)
    const effectiveTotal = !Number.isNaN(total) && total > 0 ? total : items.length
    totalLabel.textContent = `${effectiveTotal}`

    const updateCurrent = (activeIndex = null) => {
      const index = activeIndex ?? items.findIndex((item) => item.classList.contains("active"))

      currentLabel.textContent = `${Math.max((index ?? 0) + 1, 1)}`
    }

    carousel.dataset.boundCarouselCounter = "true"
    const carouselInner = carousel.querySelector(".carousel-inner")

    if (carouselInner) {
      const observer = new MutationObserver(() => {
        window.requestAnimationFrame(() => updateCurrent())
      })

      observer.observe(carouselInner, {
        subtree: true,
        attributes: true,
        attributeFilter: ["class"]
      })
    }

    carousel.addEventListener("slide.bs.carousel", (event) => {
      updateCurrent(event.to)
    })
    carousel.addEventListener("slid.bs.carousel", (event) => {
      updateCurrent(event.to)
    })

    window.setInterval(() => {
      if (!carousel.isConnected) return
      updateCurrent()
    }, 250)

    updateCurrent()
  })
}

function bindGalleryLightboxes() {
  galleryLightboxes.forEach((lightbox) => lightbox.destroy())
  galleryLightboxes = []

  document.querySelectorAll("[data-gallery-lightbox]").forEach((gallery, index) => {
    const items = Array.from(gallery.querySelectorAll("[data-gallery-lightbox-item]"))
    if (!items.length) return

    items.forEach((item) => {
      const image = item.querySelector("img")
      const fallbackWidth = Math.max(Math.round((image?.clientWidth || 400) * 3), 1200)
      const fallbackHeight = Math.max(Math.round((image?.clientHeight || 225) * 3), 900)

      item.dataset.pswpWidth = item.dataset.pswpWidth || `${fallbackWidth}`
      item.dataset.pswpHeight = item.dataset.pswpHeight || `${fallbackHeight}`

      const syncNaturalSize = (sourceImage) => {
        if (!sourceImage?.naturalWidth || !sourceImage?.naturalHeight) return

        item.dataset.pswpWidth = `${sourceImage.naturalWidth}`
        item.dataset.pswpHeight = `${sourceImage.naturalHeight}`
      }

      if (image?.complete) {
        syncNaturalSize(image)
      } else if (image) {
        image.addEventListener("load", () => syncNaturalSize(image), { once: true })
      }

      const preload = new Image()
      preload.addEventListener("load", () => syncNaturalSize(preload), { once: true })
      preload.src = item.href
    })

    if (!gallery.id) gallery.id = `gallery-lightbox-${index}`

    const lightbox = new PhotoSwipeLightbox({
      gallery: `#${gallery.id}`,
      children: "[data-gallery-lightbox-item]",
      pswpModule: PhotoSwipe,
      bgOpacity: 0.92,
      wheelToZoom: true,
      pinchToClose: true,
      doubleTapAction: "zoom"
    })

    lightbox.init()
    galleryLightboxes.push(lightbox)
  })
}

function bindTestimonialInfiniteScroll() {
  const feed = document.querySelector("[data-testimonial-feed]")
  const sentinel = document.querySelector("[data-testimonial-feed-sentinel]")
  if (!feed || !sentinel) return
  if (sentinel.dataset.boundTestimonialInfiniteScroll === "true") return

  sentinel.dataset.boundTestimonialInfiniteScroll = "true"
  sentinel.dataset.loading = "false"

  const statusText = sentinel.querySelector("[data-testimonial-feed-status-text]")
  const setState = (state) => {
    sentinel.dataset.state = state
    if (!statusText) return

    switch (state) {
      case "loading":
        statusText.textContent = sentinel.dataset.loadingText || "Loading more reviews..."
        break
      case "no-more":
        statusText.textContent = sentinel.dataset.noMoreText || "No more reviews"
        break
      case "error":
        statusText.textContent = sentinel.dataset.errorText || "Load failed. Keep scrolling to retry."
        break
      default:
        statusText.textContent = sentinel.dataset.idleText || "Keep scrolling for more reviews"
        break
    }
  }

  if (feed.querySelector(".testimonial-card")) {
    setState(sentinel.dataset.nextUrl ? "idle" : "no-more")
  } else {
    sentinel.classList.add("is-hidden")
  }

  const loadNextPage = async () => {
    const nextUrl = sentinel.dataset.nextUrl
    if (!nextUrl || sentinel.dataset.loading === "true") return

    sentinel.dataset.loading = "true"
    setState("loading")

    try {
      const response = await window.fetch(nextUrl, {
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error(`Failed to load testimonials: ${response.status}`)

      const html = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, "text/html")
      const nextFeed = doc.querySelector("[data-testimonial-feed]")
      const nextSentinel = doc.querySelector("[data-testimonial-feed-sentinel]")

      if (nextFeed) {
        nextFeed.querySelectorAll(".testimonial-card").forEach((card) => {
          feed.appendChild(card)
        })
      }

      sentinel.dataset.nextUrl = nextSentinel?.dataset.nextUrl || ""
      setState(sentinel.dataset.nextUrl ? "idle" : "no-more")
      bindGalleryLightboxes()
    } catch (error) {
      console.error(error)
      setState("error")
    } finally {
      sentinel.dataset.loading = "false"
    }
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return
      loadNextPage()
    })
  }, { rootMargin: "220px 0px" })

  observer.observe(sentinel)
}

function bindVideoSoundToggle() {
  const videoSoundButton = document.getElementById("videoSoundBtn")
  const heroVideo = document.getElementById("heroVideo")
  const videoContainer = document.getElementById("heroVideoContainer")
  const fallbackContainer = document.getElementById("heroFallbackMedia")
  const heroSection = document.getElementById("hero")

  if (!heroVideo || !videoContainer || heroVideo.dataset.boundHeroVideo === "true") return

  setHeroMediaShellReady(mediaElementIsReady(heroVideo))

  const syncVideoSoundButton = () => {
    if (!videoSoundButton) return

    const muted = heroVideo.muted
    videoSoundButton.innerHTML = muted
      ? '<i class="fas fa-volume-mute"></i>'
      : '<i class="fas fa-volume-up"></i>'
    videoSoundButton.title = muted ? "Unmute Sound" : "Mute Sound"
    videoSoundButton.setAttribute("aria-pressed", muted ? "false" : "true")
  }

  const removeGestureListeners = () => {
    document.removeEventListener("touchstart", onceFromGesture)
    document.removeEventListener("click", onceFromGesture)
    document.removeEventListener("WeixinJSBridgeReady", onceFromGesture)
  }

  const activateFallbackMedia = () => {
    if (heroVideo.dataset.heroFallbackActivated === "true") return

    heroVideo.dataset.heroFallbackActivated = "true"
    heroVideo.pause()
    removeGestureListeners()
    videoContainer.hidden = true
    if (videoSoundButton) videoSoundButton.hidden = true

    if (fallbackContainer) {
      fallbackContainer.hidden = false
      const fallbackMedia = fallbackContainer.querySelector("[data-media-reveal]")
      if (fallbackMedia && mediaElementIsReady(fallbackMedia)) {
        setHeroMediaShellReady(true)
      }
    } else if (heroSection) {
      heroSection.dataset.heroMediaMode = "background"
      setHeroMediaShellReady(true)
    }
  }

  heroVideo.dataset.boundHeroVideo = "true"
  syncVideoSoundButton()
  heroVideo.addEventListener("loadeddata", () => setHeroMediaShellReady(true), { once: true })
  heroVideo.addEventListener("canplay", () => setHeroMediaShellReady(true), { once: true })
  heroVideo.addEventListener("error", activateFallbackMedia, { once: true })
  heroVideo.addEventListener("stalled", activateFallbackMedia, { once: true })
  heroVideo.addEventListener("abort", activateFallbackMedia, { once: true })

  if (videoSoundButton && videoSoundButton.dataset.boundVideoToggle !== "true") {
    videoSoundButton.dataset.boundVideoToggle = "true"
    videoSoundButton.addEventListener("click", async (event) => {
      event.preventDefault()
      event.stopPropagation()

      if (heroVideo.paused) {
        let resumed = await attemptHeroVideoPlayback(heroVideo, { muted: false })
        if (!resumed) {
          resumed = await attemptHeroVideoPlayback(heroVideo, { muted: true })
        }

        if (!resumed) {
          activateFallbackMedia()
          return
        }

        removeGestureListeners()
      } else {
        heroVideo.muted = !heroVideo.muted
      }

      syncVideoSoundButton()
    })
  }

  const tryPlay = async () => {
    const started = await attemptHeroVideoPlayback(heroVideo, { muted: true })
    if (started) {
      syncVideoSoundButton()
      removeGestureListeners()
      return
    }

    if (!fallbackContainer) activateFallbackMedia()
  }

  tryPlay()

  async function onceFromGesture(event) {
    if (heroVideo.dataset.heroFallbackActivated === "true") return
    if (videoSoundButton && event?.target instanceof Element && videoSoundButton.contains(event.target)) return

    const started = await attemptHeroVideoPlayback(heroVideo, { muted: true })
    if (!started) {
      activateFallbackMedia()
      return
    }

    syncVideoSoundButton()
    removeGestureListeners()
  }

  document.addEventListener("WeixinJSBridgeReady", onceFromGesture, { once: true })
  document.addEventListener("touchstart", onceFromGesture, { once: true, passive: true })
  document.addEventListener("click", onceFromGesture, { once: true })
}

function initializeApplication() {
  bindLandingAnalytics()
  clearHiddenGateDraftFromPageState()
  bindMediaPlaceholders()
  bindFlashDismiss()
  bindLanguageSelector()
  bindSmoothScroll()
  bindStatsCounter()
  bindLandingPrice()
  bindInquirySubmitState()
  bindSubmitFeedbackForms()
  bindAlertMessages()
  bindCopyTextTriggers()
  bindChoiceButtons()
  bindTextareaCounters()
  bindMobileDatePlaceholders()
  bindHiddenGateForm()
  bindSnapCarousels()
  bindCarouselCounters()
  bindGalleryLightboxes()
  bindTestimonialInfiniteScroll()
  bindVideoSoundToggle()
}

document.addEventListener("turbo:load", initializeApplication)
document.addEventListener("turbo:render", initializeApplication)
document.addEventListener("DOMContentLoaded", initializeApplication, { once: true })
window.addEventListener("pageshow", initializeApplication)
